// Runtime configuration utility
// This module provides access to runtime configuration loaded from config.js

import { getBrandPackage, type BrandPackage } from "branding/registry";

interface AuthConfig {
  provider: "msal" | "auth0" | "openid";
  auth0: {
    authority: string;
    metadataAddress: string;
    clientId: string;
    audience: string;
    scopes: string;
  };
  msal: {
    authority: string;
    clientId: string;
    scopes: string;
  };
  openid: {
    authority: string;
    clientId: string;
    scopes: string;
  };
}

interface ApiConfig {
  graphqlUri: string;
  resetPasswordUri: string;
  basePath?: string; // Optional base path for API requests
  cubejsApiUrl?: string; // Cube.js analytics API URL
}

// BrandingConfig is the full brand package — getBrandingConfig() always returns a complete package.
type BrandingConfig = BrandPackage;

/** Feature keys that can be disabled via DISABLED_FEATURES / VITE_DISABLED_FEATURES */
export type FeatureKey =
  | "cveManagement"
  | "softwareSharing"
  | "userManagement"
  | "deviceClaiming"
  | "modelHierarchy"
  | "softwarePackages";

interface FeaturesConfig {
  /** List of feature keys that are disabled in this deployment */
  disabled?: FeatureKey[];
}

interface AppConfig {
  auth: AuthConfig;
  api: ApiConfig;
  // Partial: runtime config.js may not include all fields (older deployments, SWA build)
  branding?: Partial<BrandingConfig>;
  // External Auth0 organization management URI.
  // When set, the sidebar opens this URL in a new tab instead of routing to /userManagement.
  auth0OrgManagementUri?: string;
  features?: FeaturesConfig;
}

// Extend the Window interface to include our runtime config
declare global {
  interface Window {
    APP_CONFIG?: AppConfig;
    APP_BRANDING?: Partial<BrandingConfig>;
  }
}

/**
 * Get branding configuration.
 * Resolution order:
 *   1. window.APP_BRANDING (standalone override)
 *   2. window.APP_CONFIG.branding.brandId → registry lookup + runtime field merge
 *   3. import.meta.env.VITE_BRAND → registry lookup (local dev / SWA build-time)
 *   4. "knorr" default
 */
export const getBrandingConfig = (): BrandingConfig => {
  // First priority: standalone APP_BRANDING override
  if (typeof window !== "undefined" && window.APP_BRANDING) {
    const brandId = window.APP_BRANDING.brandId ?? "l-iot";
    const pkg = getBrandPackage(brandId);
    const result = { ...pkg, ...window.APP_BRANDING } as BrandingConfig;
    return result;
  }
  // Second priority: branding from APP_CONFIG (written by start.sh in Docker)
  if (typeof window !== "undefined" && window.APP_CONFIG?.branding) {
    const runtime = window.APP_CONFIG.branding;
    const brandId = runtime.brandId ?? "l-iot";
    const pkg = getBrandPackage(brandId);
    const result = { ...pkg, ...runtime } as BrandingConfig;
    return result;
  }
  // Fallback: VITE_BRAND env var (build-time for local dev and SWA deployments)
  const brandId = import.meta.env.VITE_BRAND || "l-iot";
  const pkg = getBrandPackage(brandId);

  return pkg;
};

/**
 * Get the runtime configuration with fallback to environment variables
 */
export const getRuntimeConfig = (): AppConfig => {
  // Build env-var defaults (always available at Vite build time)
  const provider =
    import.meta.env.VITE_AUTH_PROVIDER ||
    (import.meta.env.VITE_USEAUTH0 === "true" ? "auth0" : import.meta.env.VITE_OPENID_AUTHORITY ? "openid" : "msal");

  const envConfig: AppConfig = {
    auth: {
      provider: provider as "msal" | "auth0" | "openid",
      auth0: {
        authority: import.meta.env.VITE_AUTH0_AUTHORITY || "https://kb-drs-dev.eu.auth0.com",
        metadataAddress:
          (import.meta.env.VITE_AUTH0_AUTHORITY || "https://kb-drs-dev.eu.auth0.com") +
          "/.well-known/openid-configuration",
        clientId: import.meta.env.VITE_AUTH0_CLIENT_ID || "webapp",
        audience: import.meta.env.VITE_AUTH0_AUDIENCE || "https://drs-mlpa-adm/",
        scopes: import.meta.env.VITE_AUTH0_SCOPES || "openid profile email realm-info",
      },
      msal: {
        authority: `https://login.microsoftonline.com/${import.meta.env.VITE_ADAL_TENANT || ""}`,
        clientId: import.meta.env.VITE_ADAL_CLIENT_ID || "",
        scopes: import.meta.env.VITE_OPENID_SCOPES || "",
      },
      openid: {
        authority: import.meta.env.VITE_OPENID_AUTHORITY || "",
        clientId: import.meta.env.VITE_OPENID_CLIENT_ID || "",
        scopes: import.meta.env.VITE_OPENID_SCOPES || "",
      },
    },
    api: {
      graphqlUri: import.meta.env.VITE_GRAPHQL_URI || "/graphql",
      resetPasswordUri: import.meta.env.VITE_RESET_PASSWORD_URI || "",
      basePath: import.meta.env.VITE_API_BASE_PATH || "", // Optional base path for API requests
      cubejsApiUrl: import.meta.env.VITE_CUBEJS_API_URL || "",
    },
    branding: {
      brandId: import.meta.env.VITE_BRAND || "l-iot",
    },
    auth0OrgManagementUri: import.meta.env.VITE_AUTH0_ORG_MANAGEMENT_URI || "",
  };

  // Merge runtime config (window.APP_CONFIG from config.js / start.sh) on top of env defaults.
  // This lets config.js provide only branding while auth/api still fall back to VITE_* vars.
  if (typeof window !== "undefined" && window.APP_CONFIG) {
    const runtime = window.APP_CONFIG;
    const merged: AppConfig = {
      auth: runtime.auth ?? envConfig.auth,
      api: runtime.api ?? envConfig.api,
      branding: runtime.branding ?? envConfig.branding,
      auth0OrgManagementUri: runtime.auth0OrgManagementUri ?? envConfig.auth0OrgManagementUri,
    };
    return merged;
  }

  return envConfig;
};

/**
 * Get the current authentication provider configuration
 */
export const getAuthConfig = () => {
  const config = getRuntimeConfig();
  const provider = config.auth.provider;

  switch (provider) {
    case "auth0":
      return config.auth.auth0;
    case "msal":
      return config.auth.msal;
    case "openid":
      return config.auth.openid;
    default:
      throw new Error(`Unknown auth provider: ${provider}`);
  }
};

/**
 * Get the current authentication provider type
 */
export const getAuthProvider = () => {
  const provider = getRuntimeConfig().auth.provider;

  return provider;
};

/**
 * Check if Auth0 is the current provider
 */
export const isAuth0Provider = () => {
  const isAuth0 = getAuthProvider() === "auth0";
  return isAuth0;
};

/**
 * Check if MSAL is the current provider
 */
export const isMsalProvider = () => {
  const isMsal = getAuthProvider() === "msal";
  return isMsal;
};

/**
 * Check if OpenID is the current provider
 */
export const isOpenIdProvider = () => {
  const isOpenId = getAuthProvider() === "openid";
  return isOpenId;
};

/**
 * Get the Auth0 organization management URI.
 * Returns the URI string if configured, or undefined if not set.
 */
export const getAuth0OrgManagementUri = (): string | undefined => {
  const uri = getRuntimeConfig().auth0OrgManagementUri;
  return uri || undefined;
};

const VALID_FEATURE_KEYS: readonly FeatureKey[] = [
  "cveManagement",
  "userManagement",
  "modelHierarchy",
  "deviceClaiming",
  "softwarePackages",
];

/**
 * Get the set of disabled feature keys.
 * Resolution: runtime window.APP_CONFIG.features.disabled → VITE_DISABLED_FEATURES → empty set.
 */
export const getDisabledFeatures = (): Set<FeatureKey> => {
  // 1. Runtime config (Docker / config.js) – only if explicitly populated
  const runtime = typeof window !== "undefined" ? window.APP_CONFIG?.features?.disabled : undefined;
  if (runtime && Array.isArray(runtime) && runtime.length > 0) {
    const valid = runtime.filter((k): k is FeatureKey => VALID_FEATURE_KEYS.includes(k as FeatureKey));
    if (valid.length !== runtime.length) {
      console.warn(
        "[Features] Unknown feature keys in disabled list:",
        runtime.filter((k) => !VALID_FEATURE_KEYS.includes(k as FeatureKey))
      );
    }
    return new Set(valid);
  }

  // 2. Build-time env var
  const env = import.meta.env.VITE_DISABLED_FEATURES;
  if (env) {
    const keys = env
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const valid = keys.filter((k): k is FeatureKey => VALID_FEATURE_KEYS.includes(k as FeatureKey));
    if (valid.length !== keys.length) {
      console.warn(
        "[Features] Unknown feature keys in VITE_DISABLED_FEATURES:",
        keys.filter((k) => !VALID_FEATURE_KEYS.includes(k as FeatureKey))
      );
    }
    return new Set(valid);
  }

  // 3. Default: nothing disabled
  return new Set();
};

/** Check if a specific feature is enabled (i.e. NOT in the disabled list) */
export const isFeatureEnabled = (key: FeatureKey): boolean => !getDisabledFeatures().has(key);
